import i18n from "i18next";
import { initReactI18next } from "react-i18next";

import { buildBuiltinI18nResources, getGlobalLocalesForUiSelect } from "./builtinResources";
import { resolveBootstrapUiLanguage, isWorkspacePrivateLocaleAllowed } from "./bootstrapLocale";
import { EAGER_NAMESPACES, LAZY_NAMESPACES } from "./namespaces";

export { LAZY_NAMESPACES, EAGER_NAMESPACES, getGlobalLocalesForUiSelect };
export {
  GLOBAL_UI_LOCALES,
  GLOBAL_LOCALE_CODES,
  resolveGlobalLocaleMeta,
  getBuiltinNativeLabel,
} from "./localeRegistry";

/** Alinhado ao backend GET /i18n/bundle (campo catalogVersion). */
export const STATIC_CATALOG_VERSION =
  (typeof process !== "undefined" && process.env.REACT_APP_I18N_CATALOG_VERSION) || "2";

const resources = buildBuiltinI18nResources();
const supportedLngs = Object.keys(resources);
if (!supportedLngs.length) {
  console.error("[i18n] Nenhum idioma carregado; verifique localeRegistry e pastas em locales/.");
}

function fallbackLngSafe() {
  if (supportedLngs.includes("pt-BR")) return "pt-BR";
  return supportedLngs[0] || "pt-BR";
}

/** Registra locale privado (ex. it-x-it1) para i18next após validar base global (it, en, …). */
function registerPrivateUseLocale(code) {
  const c = String(code || "").trim();
  if (!c || supportedLngs.includes(c)) return;
  if (!isWorkspacePrivateLocaleAllowed(c, supportedLngs)) return;
  supportedLngs.push(c);
  if (i18n.isInitialized && Array.isArray(i18n.options.supportedLngs) && !i18n.options.supportedLngs.includes(c)) {
    i18n.options.supportedLngs = [...i18n.options.supportedLngs, c];
  }
}

export function readStoredLocale() {
  try {
    const v = localStorage.getItem("xpro4_ui_locale");
    if (v && typeof v === "string" && v.trim()) return v.trim();
  } catch {
    /* ignore */
  }
  return null;
}

/**
 * Idioma efetivo da UI: localStorage (xpro4_ui_locale) > perfil API > userData em cache > pt-BR.
 * O primeiro acesso sem preferência grava localStorage após URL / detecção do navegador (ver bootstrapLocale).
 */
export function resolveUiLocaleCode(user, userData) {
  const stored = readStoredLocale();
  if (stored) return stored;
  const u = user?.locale_code != null ? String(user.locale_code).trim() : "";
  if (u) return u;
  const ud = userData?.locale_code != null ? String(userData.locale_code).trim() : "";
  if (ud) return ud;
  return "pt-BR";
}

const earlyStored = readStoredLocale();
if (earlyStored) registerPrivateUseLocale(earlyStored);

const boot = resolveBootstrapUiLanguage(supportedLngs);
registerPrivateUseLocale(boot.code);
let initialLngSafe = supportedLngs.includes(boot.code) ? boot.code : fallbackLngSafe();
if (!supportedLngs.length) {
  initialLngSafe = "pt-BR";
} else if (boot.shouldPersist && typeof window !== "undefined" && initialLngSafe) {
  try {
    localStorage.setItem("xpro4_ui_locale", initialLngSafe);
  } catch {
    /* ignore */
  }
}

if (!i18n.isInitialized) {
  i18n.use(initReactI18next).init({
    resources,
    lng: initialLngSafe,
    fallbackLng: supportedLngs.includes("pt-BR") ? "pt-BR" : supportedLngs[0] || "pt-BR",
    supportedLngs: supportedLngs.length ? supportedLngs : ["pt-BR"],
    ns: EAGER_NAMESPACES,
    defaultNS: "common",
    interpolation: { escapeValue: false },
    react: { useSuspense: false },
  });
}

export function setUiLocale(code) {
  if (!code || typeof code !== "string") return;
  const next = code.trim();
  if (!next) return;

  registerPrivateUseLocale(next);

  const useLng = supportedLngs.includes(next)
    ? next
    : !/-x-/i.test(next)
      ? supportedLngs.find(
          (c) =>
            !/-x-/i.test(c) &&
            (next.toLowerCase() === c.toLowerCase() ||
              next.toLowerCase().startsWith(c.toLowerCase() + "-") ||
              next.toLowerCase().startsWith(c.toLowerCase() + "_"))
        )
      : null;

  if (!useLng) {
    console.warn(`[i18n] Idioma não disponível na UI: ${next}`);
    return i18n.changeLanguage(fallbackLngSafe());
  }
  try {
    localStorage.setItem("xpro4_ui_locale", useLng);
  } catch {
    /* ignore */
  }
  return i18n.changeLanguage(useLng);
}

export default i18n;
